/**
 * The minigame formats, each buildable on its own as a short round over one set, so every
 * format can be played and judged from the Games lab before the mission composer exists.
 * Multi-word question types (a trio, a river of forks, a lagoon) are built here because they
 * need the whole set; single-word types come from `makeQuestion`. A frame is a plain question
 * with `frame` data attached: the runner records what the plain question would record.
 */
import type {
  CharacterEntry,
  SceneSet,
  Sentence,
  Word,
  WordCategory,
} from '@ezishizi/content-schema';
import { COUNT_MAX, COUNT_NUMERALS } from '@ezishizi/content-schema';
import { effectiveLessonOrder } from './bands.ts';
import { canShowPicture, pictureDistractors, soundsAlike } from './distractors.ts';
import { baseSyllable, syllableInWord, toneOf } from './pinyin.ts';
import {
  anySentenceFor,
  compact,
  isWriteTarget,
  lessonCharSet,
  makeQuestion,
  makeWithFallback,
  qid,
  readSentenceFor,
  taughtSyllable,
  wordOf,
  wordPool,
  type BuildContext,
} from './questions.ts';
import { mulberry32, shuffle, type Rng } from './rng.ts';
import type { ChoiceRound, FrameData, Question } from './types.ts';

export type GameFamily = 'meet' | 'choose' | 'match' | 'build' | 'write' | 'speak';

export type GameId =
  | 'meet_compact'
  | 'meet_ten'
  | 'trio'
  | 'forks'
  | 'feed'
  | 'scene'
  | 'build'
  | 'bubbles'
  | 'count'
  | 'match_pairs'
  | 'find_all'
  | 'sort_meaning'
  | 'sort_sound'
  | 'tone_slide'
  | 'tone_paint'
  | 'builder'
  | 'bridge'
  | 'reveal'
  | 'stroke_missing'
  | 'flash'
  | 'stroke_order';

export interface GameInfo {
  id: GameId;
  label: string;
  /** A caveat shown on the lab tile (e.g. deferred to later in P1). */
  note?: string;
  blurb: string;
  family: GameFamily;
  /** Question type, frame over an existing question, or a recipe of existing questions. */
  kind: 'question' | 'frame' | 'recipe';
  /** Built before this branch (on main). */
  existing?: boolean;
  /** Only a set with write targets can play it. */
  needsWrite?: boolean;
}

export const GAMES: readonly GameInfo[] = [
  {
    id: 'meet_compact',
    label: 'Meet (compact)',
    blurb: 'One dimension at a time, retrieve between new words, three words.',
    family: 'meet',
    kind: 'recipe',
  },
  {
    id: 'meet_ten',
    label: 'Meet (ten steps)',
    blurb: 'The ten fixed steps for each word, two words.',
    family: 'meet',
    kind: 'recipe',
  },
  {
    id: 'trio',
    label: 'Three at a time',
    blurb: 'Meet three pictures, then hear one and tap it; tiles stay put, then shuffle.',
    family: 'choose',
    kind: 'question',
  },
  {
    id: 'forks',
    label: 'Boat Forks',
    blurb: 'Row up the river: at each fork, hear the word and tap the sign.',
    family: 'choose',
    kind: 'question',
  },
  {
    id: 'feed',
    label: 'Feed Shizi',
    blurb: 'Shizi says what he wants; give it to him from the tray.',
    family: 'choose',
    kind: 'question',
  },
  {
    id: 'scene',
    label: 'Which scene?',
    note: 'late P1',
    blurb: 'Hear a sentence, tap the scene it describes.',
    family: 'choose',
    kind: 'question',
  },
  {
    id: 'build',
    label: 'Listen and build',
    note: 'from lesson 6',
    blurb: 'Hear an instruction, move the object to the place it names.',
    family: 'choose',
    kind: 'question',
  },
  {
    id: 'bubbles',
    label: 'Bubble Lagoon',
    blurb: 'Pop the bubbles carrying the word Shizi calls.',
    family: 'choose',
    kind: 'question',
  },
  {
    id: 'count',
    label: 'Count and measure',
    blurb: 'Count the things, hear the phrase, tap the numeral or the measure word.',
    family: 'choose',
    kind: 'frame',
  },
  {
    id: 'match_pairs',
    label: 'Match pairs',
    blurb: 'Three pictures, three words, tap to pair them.',
    family: 'match',
    kind: 'question',
    existing: true,
  },
  {
    id: 'find_all',
    label: 'Find them all',
    blurb: 'Tap every copy of the character among look-alikes.',
    family: 'match',
    kind: 'question',
    existing: true,
  },
  {
    id: 'sort_meaning',
    label: 'Sort by meaning',
    blurb: 'Flick each card into the basket of its kind.',
    family: 'match',
    kind: 'frame',
  },
  {
    id: 'sort_sound',
    label: 'Shell sort by sound',
    blurb: 'Hear a shell, sort it into the basket of the word it says — four to choose from.',
    family: 'match',
    kind: 'question',
  },
  {
    id: 'tone_slide',
    label: 'Tone slide',
    blurb: 'Hear the syllable, slide along its tone.',
    family: 'match',
    kind: 'frame',
  },
  {
    id: 'tone_paint',
    label: 'Tone painter',
    blurb: 'Hear a syllable, then paint its tone: arrows first, then draw it with a finger.',
    family: 'match',
    kind: 'question',
  },
  {
    id: 'builder',
    label: 'Character builder',
    blurb: 'Drag the parts together to make the character.',
    family: 'build',
    kind: 'frame',
  },
  {
    id: 'bridge',
    label: 'Sentence bridge',
    blurb: 'Put the planks in order to build the bridge.',
    family: 'build',
    kind: 'frame',
  },
  {
    id: 'reveal',
    label: 'Reveal by writing',
    blurb: 'Write from memory; each stroke uncovers the picture.',
    family: 'write',
    kind: 'frame',
    needsWrite: true,
  },
  {
    id: 'stroke_missing',
    label: 'Missing stroke',
    blurb: 'One stroke is missing; draw it.',
    family: 'write',
    kind: 'question',
    needsWrite: true,
  },
  {
    id: 'flash',
    label: 'Flash and recall',
    blurb: 'See the character for a moment, then write it.',
    family: 'write',
    kind: 'recipe',
    needsWrite: true,
  },
  {
    id: 'stroke_order',
    label: 'Stroke order sort',
    blurb: 'Tap the strokes in writing order.',
    family: 'write',
    kind: 'question',
    needsWrite: true,
  },
];

export const GAME_IDS: readonly GameId[] = GAMES.map((g) => g.id);
export const isGameId = (v: unknown): v is GameId => GAME_IDS.includes(v as GameId);

const NUMERAL_VALUE: Record<string, number> = {
  一: 1,
  两: 2,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
  十: 10,
};
const MEASURE_WORDS = ['个', '只', '把', '条', '支', '粒', '朵', '片', '双', '件', '串', '本'];

/** Basket pairs whose categories never overlap for a P1 word (a fish is animal and food). */
/** Basket pairs that contrast cleanly at P1 (a child can say why a card goes where). */
const SORT_PAIRS: Array<[WordCategory, WordCategory]> = [
  ['food', 'animal'],
  ['person', 'thing'],
  ['body', 'clothing'],
  ['family', 'animal'],
  ['family', 'thing'],
];

/** Every arrangement of a short list. Only ever called with five items or fewer. */
function permutations<T>(xs: readonly T[]): T[][] {
  if (xs.length <= 1) return [[...xs]];
  return xs.flatMap((x, i) =>
    permutations([...xs.slice(0, i), ...xs.slice(i + 1)]).map((rest) => [x, ...rest]),
  );
}

const pictureWords = (ctx: BuildContext, entries: CharacterEntry[]): Word[] => {
  const seen = new Set<string>();
  const out: Word[] = [];
  for (const e of entries) {
    const w = wordOf(ctx, e);
    if (w && canShowPicture(w) && !seen.has(w.id)) {
      seen.add(w.id);
      out.push(w);
    }
  }
  return out;
};

const entryFor = (entries: CharacterEntry[], w: Word): CharacterEntry | undefined =>
  entries.find((e) => e.wordId === w.id);

/** The entry and word a per-ask question is scored against: the ask's target when it is one
 * of the set's words, else the round's anchor (a distractor from the wider pool). */
function targetOf(
  ctx: BuildContext,
  entries: CharacterEntry[],
  wordId: string,
  anchor: CharacterEntry,
  anchorWord: Word,
): { entry: CharacterEntry; word: Word } {
  const word = ctx.bundle.words[wordId];
  const entry = word ? entryFor(entries, word) : undefined;
  return entry && word ? { entry, word } : { entry: anchor, word: anchorWord };
}

/** Every word of the lesson with a picture (the pool for baskets, fillers and rings). */
const lessonPictureWords = (ctx: BuildContext): Word[] =>
  Object.values(ctx.bundle.words).filter(canShowPicture);

/**
 * The words taught by the end of this lesson that have a picture: the pool for anything a round
 * asks about, as opposed to a decoy. Falls back to the whole bundle for one built before it
 * listed its known words.
 */
const knownPictureWords = (ctx: BuildContext): Word[] => {
  const known = ctx.bundle.knownWordIds ?? [];
  if (known.length === 0) return lessonPictureWords(ctx);
  return known
    .map((id) => ctx.bundle.words[id])
    .filter((w): w is Word => w !== undefined && canShowPicture(w));
};

/** A choose/deliver round list (Boat Forks, Feed Shizi) over the set's picture words. */
function chooseRounds(
  ctx: BuildContext,
  rng: Rng,
  entries: CharacterEntry[],
  count: number,
  hanziLast: boolean,
): ChoiceRound[] {
  const words = pictureWords(ctx, entries);
  if (words.length < 2) return [];
  const order = shuffle(words, rng);
  const rounds: ChoiceRound[] = [];
  for (let i = 0; i < count; i++) {
    const target = order[i % order.length]!;
    const e = entryFor(entries, target);
    const n = i < 2 ? 2 : 3;
    const syllable = e ? taughtSyllable(ctx, e, target) : null;
    const pool = e ? wordPool(ctx, e) : words;
    // Forks three and four (Boat Forks) put a sound-alike on one sign where the lesson has one;
    // the first two and the hanzi fork stay clean.
    const near =
      hanziLast && i >= 2 && i < count - 1
        ? pool.filter(
            (w) => w.id !== target.id && canShowPicture(w) && soundsAlike(target, w, syllable),
          )
        : [];
    const nearPick = near.length ? [near[Math.floor(rng() * near.length)]!] : [];
    const rest = pictureDistractors(
      target,
      pool.filter((w) => !nearPick.some((x) => x.id === w.id)),
      n - 1 - nearPick.length,
      rng,
      { avoidSyllable: syllable },
    );
    const d = [...nearPick, ...rest];
    if (d.length === 0) continue;
    rounds.push({
      answerId: target.id,
      choices: shuffle([target, ...d], rng),
      hanzi: hanziLast && i === count - 1,
    });
  }
  return rounds;
}

/**
 * Feed Shizi's rounds: the set's food words as targets (cycled), tray items from the level's
 * food, four items from the third feed, and from the third feed the word's sentence as the
 * request where the set has one ("我有鱼" rather than "鱼").
 */
function feedRounds(
  ctx: BuildContext,
  rng: Rng,
  entries: CharacterEntry[],
  setFood: Word[],
  levelFood: Word[],
  count: number,
): ChoiceRound[] {
  const pool = [...setFood, ...levelFood];
  if (pool.length < 2) return [];
  /**
   * Who Shizi asks for, feed by feed.
   *
   * Most sets teach exactly one food — lesson 3 is 米饭 and nothing else, and so are lessons 7,
   * 9, 10, 12 and 15 in four of their sets — so taking every target from the set made all six
   * feeds the same word. The child stops listening after the second one and just taps the rice.
   * The lesson's other foods take the turns in between, so the answer changes every feed while
   * the set's own words still come round twice as often as any other. A set that teaches two or
   * more foods never reaches the fallback and stays entirely its own.
   */
  const own = shuffle(setFood, rng);
  const rest = shuffle(levelFood, rng);
  const order: Word[] = [];
  for (let i = 0, oi = 0, ri = 0; i < count; i++) {
    const prev = order[order.length - 1];
    const next =
      own[oi % own.length]!.id !== prev?.id
        ? own[oi++ % own.length]!
        : rest.length
          ? rest[ri++ % rest.length]!
          : null;
    if (!next) break;
    order.push(next);
  }
  const rounds: ChoiceRound[] = [];
  for (let i = 0; i < order.length; i++) {
    const target = order[i]!;
    const n = Math.min(pool.length, i < 2 ? 2 : i < 4 ? 3 : 4);
    const e = entryFor(entries, target);
    const d = pictureDistractors(
      target,
      pool.filter((w) => w.id !== target.id),
      n - 1,
      rng,
      { avoidSyllable: e ? taughtSyllable(ctx, e, target) : null },
    );
    if (d.length === 0) continue;
    const phrase = i >= 2 ? (anySentenceFor(ctx, target)?.audioId ?? null) : null;
    rounds.push({
      answerId: target.id,
      choices: shuffle([target, ...d], rng),
      hanzi: false,
      phrase,
    });
  }
  return rounds;
}

/** The syllable each character of a word carries, toneless, for sound-alike pairing. */
const syllablesOf = (w: Word): string[] =>
  w.pinyin
    .trim()
    .split(/\s+/)
    .map((s) => baseSyllable(s));

/**
 * A partner for Shell sort by sound: a lesson word with a picture that shares the taught
 * syllable in another tone (鱼 yú beside 雨 yǔ), else one sharing its initial or final; never
 * a homophone, which the prompt could not tell apart. Returns the partner and the contrast.
 */
export function soundalikePartner(
  ctx: BuildContext,
  e: CharacterEntry,
  word: Word,
): { partner: Word; contrast: string } | null {
  const syllable = taughtSyllable(ctx, e, word);
  if (!syllable) return null;
  const base = baseSyllable(syllable);
  const tone = toneOf(syllable);
  const pool = lessonPictureWords(ctx).filter((w) => w.id !== word.id && w.text !== word.text);
  let best: { partner: Word; contrast: string; score: number } | null = null;
  for (const w of pool) {
    if (soundsAlike(word, w)) continue; // identical apart from tone across the whole word: unfair
    const chars = [...w.text];
    const syls = w.pinyin.trim().split(/\s+/);
    for (let i = 0; i < syls.length; i++) {
      const s = syls[i]!;
      const b = baseSyllable(s);
      const t = toneOf(s);
      let score = 0;
      let contrast = '';
      if (b === base && t !== tone && t !== 0 && tone !== 0) {
        score = 3;
        contrast = `${syllable} / ${s}`;
      } else if (b !== base && b.slice(0, 1) === base.slice(0, 1) && b.length > 1) {
        score = 1;
        contrast = `${syllable} / ${s}`;
      } else if (b !== base && b.slice(-2) === base.slice(-2)) {
        score = 1;
        contrast = `${syllable} / ${s}`;
      }
      if (score > (best?.score ?? 0) && chars.length === syls.length)
        best = { partner: w, contrast, score };
    }
  }
  return best ? { partner: best.partner, contrast: best.contrast } : null;
}

const withBeat = (q: Question | null, beat: string | null): Question | null =>
  q && beat ? { ...q, beat } : q;

const withFrame = (q: Question | null, frame: FrameData): Question | null =>
  q ? { ...q, frame } : null;

/** Content tokens of a sentence (punctuation is not a plank). */
export const contentTokenIdx = (tokens: Array<{ text: string }>): number[] =>
  tokens.map((t, i) => (/[一-鿿]/.test(t.text) ? i : -1)).filter((i) => i >= 0);

/** A shuffle of the plank order that is never the answer. */
/** Tokens a plank should never start with: they belong to the word before them. */
const PARTICLES = new Set(['的', '了', '吗', '呢', '啊', '吧', '着', '过']);

/**
 * Split content-token indices into `n` contiguous planks of near-equal length. A plank never
 * starts with a particle when the split can move one token either way (这是我的 | 衣服 rather
 * than 这是我 | 的衣服); `texts` gives each token's text by index.
 */
export function chunkTokens(idx: number[], n: number, texts: string[] = []): number[][] {
  const count = Math.max(1, Math.min(n, idx.length));
  const out: number[][] = [];
  let at = 0;
  for (let k = 0; k < count; k++) {
    let size = Math.ceil((idx.length - at) / (count - k));
    if (k < count - 1) {
      const startsWithParticle = (cut: number) => PARTICLES.has(texts[idx[cut]!] ?? '');
      const remaining = idx.length - at;
      // Shift the cut by one, later first, as long as every later plank keeps at least one
      // token.
      for (const shift of [1, -1]) {
        if (!startsWithParticle(at + size)) break;
        const alt = size + shift;
        if (alt >= 1 && remaining - alt >= count - k - 1 && !startsWithParticle(at + alt))
          size = alt;
      }
    }
    out.push(idx.slice(at, at + size));
    at += size;
  }
  return out;
}

function scrambled(idx: number[], rng: Rng): number[] {
  if (idx.length < 2) return idx;
  for (let k = 0; k < 10; k++) {
    const s = shuffle(idx, rng);
    if (s.some((v, i) => v !== idx[i])) return s;
  }
  return [...idx.slice(1), idx[0]!];
}

/**
 * One short round of a game over one set: three to ten questions, deterministic per seed.
 * Empty when the set has nothing the game can use (no write targets, no components, no
 * scene content), which the lab shows as unavailable.
 */
export function buildGameRound(
  ctx: BuildContext,
  entries: CharacterEntry[],
  game: GameId,
): Question[] {
  const rng = mulberry32(`${ctx.seed}:game:${game}`);
  const q = (type: Parameters<typeof makeQuestion>[2], e: CharacterEntry, opts = {}) =>
    makeQuestion(ctx, rng, type, e, opts);
  const qf = (type: Parameters<typeof makeQuestion>[2], e: CharacterEntry, opts = {}) =>
    makeWithFallback(ctx, rng, type, e, opts);
  const writers = entries.filter((e) => isWriteTarget(e, ctx.higherChinese));
  const anchor = entries[0];
  if (!anchor) return [];
  const anchorWord = wordOf(ctx, anchor);

  switch (game) {
    case 'meet_compact': {
      // Two stages, nothing retrieved twice (the stops after this one test the words): meet each
      // word as a picture with one quick check right after it, then meet each as a hanzi, then the
      // first one's pinyin and say it. Eleven screens for three words — it was twenty-two, and the
      // Meet was a third of every island.
      const [a, b, c] = entries;
      if (!a) return [];
      const out: Array<Question | null> = [];
      const ws = [a, b, c].filter((e): e is CharacterEntry => !!e);
      ws.forEach((e, i) =>
        out.push(
          q('introduce', e, { show: 'picture' }),
          qf('audio_to_picture', e, { choices: i === 0 ? 2 : 3 }),
        ),
      );
      // Stage beats: Shizi announces the change of layer before the first hanzi card and
      // before the Speak, so the recipe reads as three stages rather than one long run.
      ws.forEach((e, i) =>
        out.push(withBeat(q('introduce', e, { show: 'hanzi' }), i === 0 ? 'stage_hanzi' : null)),
      );
      out.push(q('introduce', a, { show: 'pinyin' }), withBeat(q('speak', a), 'stage_say'));
      return compact(out);
    }
    case 'meet_ten': {
      // Each word as a picture, checked, then as a hanzi, checked; then the first one's pinyin
      // and a say-it. Ten screens for two words (it was twenty).
      const out: Array<Question | null> = [];
      const ws = entries.slice(0, 2);
      for (const e of ws)
        out.push(
          q('introduce', e, { show: 'picture' }),
          qf('audio_to_picture', e, { choices: 2 }),
          q('introduce', e, { show: 'hanzi' }),
          qf('audio_to_hanzi', e, { choices: 2 }),
        );
      out.push(q('introduce', anchor, { show: 'pinyin' }), q('speak', anchor));
      return compact(out);
    }
    case 'trio': {
      // One group of three: the intro card, six fixed-position asks (each word twice, never the
      // same twice running), then three with the tiles shuffled.
      const words = pictureWords(ctx, entries);
      const group: Word[] = [];
      const syl = (w: Word) => {
        const e = entryFor(entries, w);
        return e ? taughtSyllable(ctx, e, w) : null;
      };
      // Two words that share a taught syllable in any tone never sit in one trio: heard side by
      // side, the child could only tell them apart by tone.
      for (const w of words) {
        if (group.length >= 3) break;
        if (group.some((g) => soundsAlike(w, g, syl(w)) || soundsAlike(g, w, syl(g)))) continue;
        group.push(w);
      }
      if (group.length < 3) return [];
      const es = group.map((w) => entryFor(entries, w)!);
      const intro: Question = {
        id: qid('introduce_trio', es[0]!),
        entry: es[0]!,
        word: group[0]!,
        skill: 'meaning',
        type: 'introduce_trio',
        words: group,
      };
      const asks: Array<Question | null> = [intro];
      // Two shuffled passes over the three words; the second pass is rotated when it would start
      // with the word the first ended on. (Picking one at a time can dead-end with only the
      // last-asked word left.)
      // Five tiles: the three heard words plus two silent distractors from the set (never heard,
      // never a sound-alike of a heard word). Asks one to three keep the layout so the child
      // learns where things are; from ask four every ask reshuffles; asks seven to nine show the
      // heard words as hanzi (the distractors keep their pictures, so hanzi is not a giveaway).
      const silent = pictureDistractors(
        group[0]!,
        words.filter((w) => !group.some((g) => g.id === w.id)),
        2,
        rng,
        { avoidSyllable: syl(group[0]!) },
      ).filter((w) => !group.some((g) => soundsAlike(w, g, syl(w)) || soundsAlike(g, w, syl(g))));
      const tiles = shuffle([...group, ...silent], rng);
      const first = shuffle([0, 1, 2], rng);
      const second = shuffle([0, 1, 2], rng);
      if (second[0] === first[2]) second.push(second.shift()!);
      const third = shuffle([0, 1, 2], rng);
      if (third[0] === second[2]) third.push(third.shift()!);
      for (const i of first) asks.push(q('audio_to_picture', es[i]!, { fixedChoices: tiles }));
      for (const i of second)
        asks.push(q('audio_to_picture', es[i]!, { fixedChoices: shuffle(tiles, rng) }));
      const hanziIds = group.map((w) => w.id);
      for (const i of third)
        asks.push(q('audio_to_picture', es[i]!, { fixedChoices: shuffle(tiles, rng), hanziIds }));
      return compact(asks);
    }
    case 'forks': {
      const forks = chooseRounds(ctx, rng, entries, 5, true);
      if (forks.length < 3 || !anchorWord) return [];
      return forks.map((f, step) => {
        const { entry, word } = targetOf(ctx, entries, f.answerId, anchor, anchorWord);
        return {
          id: `${qid('listen_forks', anchor)}:${step}`,
          entry,
          word,
          skill: 'listen',
          type: 'listen_forks',
          forks,
          step,
        };
      });
    }
    case 'feed': {
      // Shizi eats: only sets with something to feed him. The set's food and drink words are
      // asked first, the rest of the level's food fills the tray.
      const edible = (w: Word) => w.category === 'food' || w.category === 'drink';
      const setFood = pictureWords(ctx, entries).filter(edible);
      if (setFood.length === 0 || !anchorWord) return [];
      const levelFood = lessonPictureWords(ctx).filter(
        (w) => edible(w) && !setFood.some((x) => x.id === w.id),
      );
      const rounds = feedRounds(ctx, rng, entries, setFood, shuffle(levelFood, rng), 6);
      if (rounds.length < 3) return [];
      return rounds.map((r, step) => {
        const { entry, word } = targetOf(ctx, entries, r.answerId, anchor, anchorWord);
        return {
          id: `${qid('listen_feed', anchor)}:${step}`,
          entry,
          word,
          skill: 'listen',
          type: 'listen_feed',
          rounds,
          step,
        };
      });
    }
    case 'scene': {
      // A scene's sentence is often written past the set it illustrates (the fish scene's
      // sentence is plain, but the person scene's sentence also names 口, a set-2 character on a
      // lesson where the fish scene is set 1): playing it before the child has met every set it
      // touches means asking about a character never taught. `reached` is how far this round has
      // got, taken from the sets its own entries belong to rather than hard-coded to "the round's
      // set" so a mixed review round still works; a set-1 round reaches only set 1, so any
      // sentence that steps into set 2 is off limits until a later round reaches it. This guard is
      // absolute: nothing that steps past what the round has reached is ever playable.
      //
      // Once that leaves a set of playable scenes, a round prefers the ones that use one of its
      // own words — otherwise set 2 would happily replay set 1's fish scene just because it is now
      // allowed to. But a lesson does not owe every set a scene of its own (lesson 3's set 3 has
      // none), and an empty island is worse than a review: if no playable scene touches this
      // round's own words, fall back to the rest of the playable ones. Everything in them is
      // already taught, by the first guard, so a review scene is always a safe fallback, never a
      // guess.
      const setIndexOf = (wordId: string | null): number | null => {
        if (!wordId) return null;
        const idx = ctx.bundle.lesson.sets.findIndex((s) =>
          s.entries.some((x) => x.wordId === wordId),
        );
        return idx === -1 ? null : idx;
      };
      const reached = entries.reduce((max, x) => {
        const idx = setIndexOf(x.wordId);
        return idx === null ? max : Math.max(max, idx);
      }, -1);
      const playable: Array<{ set: SceneSet; sentence: Sentence }> = [];
      for (const set of Object.values(ctx.bundle.sceneSets)) {
        const sentence = ctx.bundle.sentences[set.sentenceId];
        if (!sentence) continue;
        const tokenSetIndices = sentence.tokens
          .map((t) => setIndexOf(t.wordId))
          .filter((i): i is number => i !== null);
        if (tokenSetIndices.some((i) => i > reached)) continue;
        playable.push({ set, sentence });
      }
      const ownsAWord = (sentence: Sentence) =>
        sentence.tokens.some((t) => entries.some((x) => x.wordId === t.wordId));
      const own = playable.filter((x) => ownsAWord(x.sentence));
      const chosen = own.length > 0 ? own : playable;
      const out: Question[] = [];
      for (const { set, sentence } of chosen) {
        // The question belongs to the first taught character in the sentence.
        const e =
          entries.find((x) => sentence.tokens.some((t) => t.wordId === x.wordId)) ??
          Object.values(ctx.bundle.lesson.sets)
            .flatMap((s) => s.entries)
            .find((x) => sentence.tokens.some((t) => t.wordId === x.wordId));
        const w = e ? wordOf(ctx, e) : null;
        if (!e || !w) continue;
        out.push({
          id: qid('listen_scene', e),
          entry: e,
          word: w,
          skill: 'sentence',
          type: 'listen_scene',
          sentence,
          set,
        });
      }
      return shuffle(out, rng);
    }
    case 'build': {
      // The set's composed tasks (see content-schema `composeBuildTasks`): Chinese-only
      // sentences with two things to decode, offered from lesson 6.
      const setId = ctx.bundle.lesson.sets.find((s) => s.entries.includes(anchor))?.id;
      const out: Question[] = [];
      for (const task of Object.values(ctx.bundle.builds)) {
        if (setId && task.setId !== setId) continue;
        const objects = task.objectIds
          .map((id) => ctx.bundle.words[id])
          .filter((w): w is Word => w !== undefined);
        if (objects.length !== task.objectIds.length) continue;
        const firstTarget = task.targets[0]!.objectId;
        const target = ctx.bundle.words[firstTarget];
        const e =
          entries.find((x) => x.wordId === firstTarget) ??
          ctx.bundle.lesson.sets.flatMap((s) => s.entries).find((x) => x.wordId === firstTarget) ??
          anchor;
        if (!target) continue;
        out.push({
          id: qid('listen_build', e),
          entry: e,
          word: target,
          skill: 'sentence',
          type: 'listen_build',
          task,
          objects,
          cue: false,
        });
      }
      // One-placement tasks first, the two-part ones after; the first task speaks the format's
      // English cue (the `listen_build` instruction line).
      const ordered = [
        ...shuffle(
          out.filter((x) => x.type === 'listen_build' && x.task.targets.length === 1),
          rng,
        ),
        ...shuffle(
          out.filter((x) => x.type === 'listen_build' && x.task.targets.length > 1),
          rng,
        ),
      ];
      return ordered.map((x, i) => (i === 0 ? { ...x, cue: true } : x));
    }
    case 'bubbles': {
      // Five callable words rather than four: with only four the child learns the lagoon's
      // faces by the second round and stops listening.
      const words = pictureWords(ctx, entries).slice(0, 4);
      if (words.length < 3 || !anchorWord) return [];
      // Two words a child could mishear as one another must not be called back to back. Set 1
      // is 衣鱼雨耳牙, so a plain shuffle happily produced 鱼 yú then 雨 yǔ, and lesson 3 gives
      // 河 hé then 禾 hé — to a listener the game had just said the same word twice.
      const clash = (a: Word, b: Word): boolean => {
        if (a.id === b.id) return true;
        const e = entryFor(entries, a);
        return soundsAlike(a, b, e ? taughtSyllable(ctx, e, a) : null);
      };
      /**
       * An order with no clashing neighbours that also does not open on `after`. A round is at
       * most five words, so every arrangement is enumerated and one of the best is taken at
       * random: rejection-sampling a shuffle looked simpler but quietly failed on the three-word
       * sets, where only one arrangement in six satisfies both constraints.
       */
      const spaced = (after: Word | undefined, avoid: Word[] | undefined): Word[] => {
        const all = permutations(words);
        const key = (o: Word[]) => o.map((w) => w.id).join();
        const settled = (o: Word[]) => o.every((w, i) => i === 0 || !clash(o[i - 1]!, w));
        const opensWell = (o: Word[]) => !after || !clash(after, o[0]!);
        const notSameWord = (o: Word[]) => !after || after.id !== o[0]!.id;
        const fresh = (o: Word[]) => !avoid || key(o) !== key(avoid);
        // A three-word set with a sound-alike pair cannot satisfy everything at once: the odd
        // word out can separate the pair inside each round, but then the two rounds have to
        // meet pair-to-pair at the join, and the only arrangement avoiding that is the one the
        // first round already used. So the wishes are given up in a deliberate order — never
        // repeat the identical word, then keep the two rounds different, and only then worry
        // about a sound-alike neighbour, which is after all the contrast the game teaches.
        // Larger sets (every authored one) satisfy the lot.
        const tiers = [
          all.filter((o) => settled(o) && opensWell(o) && fresh(o)),
          all.filter((o) => settled(o) && notSameWord(o) && fresh(o)),
          // `opensWell` implies `notSameWord`, so this keeps the join clean and spends the one
          // unavoidable sound-alike neighbour inside the round instead of across it.
          all.filter((o) => opensWell(o) && fresh(o)),
          all.filter((o) => notSameWord(o) && fresh(o)),
          all.filter((o) => settled(o) && notSameWord(o)),
          all.filter(notSameWord),
          all,
        ];
        const pick = tiers.find((xs) => xs.length > 0)!;
        return pick[Math.floor(rng() * pick.length)]!;
      };
      // The two rounds get their own orders, and the second does not open on the word the
      // first ended with: before this they were the same array, so the whole second half was a
      // note-for-note replay of the first.
      const first = spaced(undefined, undefined);
      const second = spaced(first[first.length - 1], first);
      // One bubble per call. It used to be two, and that — not the shuffle — is what "it says
      // the same word twice in a row" was: every single call sent the child after the same word
      // twice, and because the word is spoken once, when the question mounts, the second hunt
      // was silent. One pop per call means every hunt is a new word and every hunt is spoken.
      const callsFor = (order: Word[]) => order.map((w) => ({ wordId: w.id }));
      // Everything else the lesson can picture floats about uncalled, sound-alikes first
      // because a near-miss is the contrast this game teaches. Five slots drawing on a pool
      // this wide means the lagoon is a different handful every call, rather than the same
      // three or four faces in rotation.
      const decoys: Word[] = [];
      const spare = shuffle(
        lessonPictureWords(ctx).filter((x) => !words.some((y) => y.id === x.id)),
        rng,
      );
      const takeDecoy = (x: Word) => {
        if (decoys.length < 6 && !decoys.some((y) => y.id === x.id)) decoys.push(x);
      };
      for (const w of words) {
        const e = entryFor(entries, w);
        const sy = e ? taughtSyllable(ctx, e, w) : null;
        for (const x of spare) if (soundsAlike(w, x, sy)) takeDecoy(x);
      }
      for (const x of spare) takeDecoy(x);
      const round = (tag: string, calls: Array<{ wordId: string }>): Question[] =>
        calls.map((c, step) => {
          const { entry, word } = targetOf(ctx, entries, c.wordId, anchor, anchorWord);
          return {
            id: `${qid('listen_bubbles', anchor)}:${tag}:${step}`,
            entry,
            word,
            skill: 'listen',
            type: 'listen_bubbles',
            words,
            calls,
            step,
            // Pictures for the first two calls of the first round, hanzi after that.
            showHanzi: tag !== 'a' || step >= 2,
            decoys,
          };
        });
      return [...round('a', callsFor(first)), ...round('b', callsFor(second))];
    }
    case 'count': {
      const out: Question[] = [];
      const taughtNumerals = Object.keys(ctx.bundle.characters).filter((c) => c in NUMERAL_VALUE);
      const nouns = Object.values(ctx.bundle.words).filter((w) => w.measure && canShowPicture(w));
      for (const e of entries) {
        const value = NUMERAL_VALUE[e.char];
        if (value !== undefined) {
          if (value > COUNT_MAX) continue;
          // "How many? Tap the number" needs something to count: with one object on the board
          // there is nothing to work out, so 一 gets the `hear` variant below (where the count
          // has to be decoded from the phrase) and never the numeral one.
          const askable = value >= 2;
          const others = shuffle(
            taughtNumerals.filter((c) => c !== e.char && NUMERAL_VALUE[c] !== value),
            rng,
          ).slice(0, 2);
          if (others.length < 2) continue;
          const candidates = nouns.filter((w) => w.measure && w.measure in ctx.bundle.characters);
          const noun = candidates[Math.floor(rng() * candidates.length)];
          if (!noun) continue;
          const base = q('audio_to_hanzi', e);
          if (!base || base.type !== 'audio_to_hanzi') continue;
          if (askable)
            out.push({
              ...base,
              choices: shuffle([e.char, ...others], rng),
              frame: { frame: 'count', n: value, noun, measure: noun.measure!, variant: 'numeral' },
            });
          // Then the other way round: hear 三条鱼, tap the plate with three. The plates are
          // labelled with the numeral characters (一 二 三 四 五), but the phrase for two says 两,
          // not 二 — decoding "两条鱼" onto a plate marked 二 would ask the child to already know a
          // character 二's own entry never taught them. Only build this half when the entry's
          // character is the one actually spoken for its count; 二 keeps its `numeral` question
          // above and loses only this one.
          const NUMERAL_CHAR = ['', '一', '二', '三', '四', '五'];
          if (COUNT_NUMERALS[value] === e.char) {
            const otherCounts = shuffle(
              [1, 2, 3, 4, 5].filter((v) => v !== value),
              rng,
            ).slice(0, 2);
            const plates = shuffle([value, ...otherCounts], rng);
            const hearNoun = candidates[Math.floor(rng() * candidates.length)] ?? noun;
            out.push({
              ...base,
              id: `${base.id}:hear`,
              choices: plates.map((v) => NUMERAL_CHAR[v]!),
              frame: {
                frame: 'count',
                n: value,
                noun: hearNoun,
                measure: hearNoun.measure!,
                variant: 'hear',
                plates,
              },
            });
          }
        } else if (MEASURE_WORDS.includes(e.char)) {
          const noun = nouns.find((w) => w.measure === e.char);
          if (!noun) continue;
          const others = shuffle(
            MEASURE_WORDS.filter((c) => c !== e.char && c in ctx.bundle.characters),
            rng,
          ).slice(0, 2);
          if (others.length < 2) continue;
          const base = q('audio_to_hanzi', e);
          if (!base || base.type !== 'audio_to_hanzi') continue;
          out.push({
            ...base,
            choices: shuffle([e.char, ...others], rng),
            frame: { frame: 'count', n: 1, noun, measure: e.char, variant: 'measure' },
          });
        }
      }
      return out;
    }
    case 'match_pairs':
      // Five pairs from lesson 3, three before that. A pair board needs the entry's word to have
      // a picture and two picture distractors, and not every entry does — so build across all of
      // them and take the three that came out, rather than slicing three at random and letting
      // the misses compact away. Sliced first, a set whose entries mostly miss returned an empty
      // round on close to half its seeds.
      return compact(
        shuffle(entries, rng).map((e) =>
          q('match_pairs', e, {
            pairs:
              effectiveLessonOrder(ctx.bundle.lesson.levelId, ctx.bundle.lesson.order) >= 3 ? 5 : 3,
          }),
        ),
      ).slice(0, 3);
    case 'find_all': {
      // Twenty tiles every board (five rows of four: two more rows than the nine-tile grid the
      // learn path uses). Sixteen still read at a glance — the target was found before the child
      // had really looked at anything — so the board grew a row and the hunt grew a target with
      // it. The ramp is in the prompt: the character, then the character hidden after two
      // seconds (recall, with a peek), then its pinyin only.
      const prompts = ['hanzi', 'hidden', 'pinyin', 'hidden'] as const;
      return compact(
        entries
          .slice(0, 4)
          .map((e, i) =>
            q('find_all', e, { grid: { size: 20, targets: i === 0 ? 4 : 5, prompt: prompts[i] } }),
          ),
      );
    }
    case 'sort_meaning': {
      // Every card is something the child is asked to sort, so it comes from words already
      // taught — never the rest of the level the bundle carries as a distractor pool. The deck
      // used to be eight cards drawn from the whole level: lesson 5 set 1 dealt 木瓜, 羊肉串, 米饭
      // and not one word of its own, and a child on that island was sorting other lessons.
      const all = knownPictureWords(ctx);
      const setWords = pictureWords(ctx, entries);
      const ofCat = (c: WordCategory) => all.filter((w) => w.category === c);
      // The pair that covers most of the set with three picture words on each side.
      let best: { pair: [WordCategory, WordCategory]; covered: number } | null = null;
      for (const pair of SORT_PAIRS) {
        if (ofCat(pair[0]).length < 3 || ofCat(pair[1]).length < 3) continue;
        const covered = setWords.filter(
          (w) => w.category === pair[0] || w.category === pair[1],
        ).length;
        if (covered > (best?.covered ?? 0)) best = { pair, covered };
      }
      // A set with fewer than two words of its own to sort is not this game's: the deck would
      // be review of earlier lessons with the set's one word in it. The stop picks another
      // reading format instead.
      if (!best || best.covered < 2) return [];
      const [left, right] = best.pair;
      const inPair = (w: Word) => w.category === left || w.category === right;
      // The set's own words are all dealt, then the rest of this lesson's, then earlier lessons'.
      const lessonIds = new Set(
        ctx.bundle.lesson.sets.flatMap((s) => s.entries.map((x) => x.wordId)),
      );
      const own = setWords.filter(inPair);
      const rest = all.filter((w) => inPair(w) && !own.some((x) => x.id === w.id));
      const cards = shuffle(
        [
          ...own,
          ...shuffle(
            rest.filter((w) => lessonIds.has(w.id)),
            rng,
          ),
          ...shuffle(
            rest.filter((w) => !lessonIds.has(w.id)),
            rng,
          ),
        ].slice(0, 6),
        rng,
      );
      const out: Question[] = [];
      for (const w of cards) {
        const e =
          entryFor(entries, w) ??
          ctx.bundle.lesson.sets.flatMap((s) => s.entries).find((x) => x.wordId === w.id) ??
          anchor;
        const base = qf('hanzi_to_picture', e);
        if (!base) continue;
        const leftWord = ofCat(left).find((x) => x.id !== w.id)!;
        const rightWord = ofCat(right).find((x) => x.id !== w.id)!;
        out.push({
          ...base,
          word: w,
          frame: {
            frame: 'sort_meaning',
            left,
            right,
            leftWord,
            rightWord,
            answer: w.category === left ? 'left' : 'right',
            intro: out.length === 0,
          },
        });
      }
      return out;
    }
    case 'sort_sound': {
      const out: Question[] = [];
      for (const e of entries) {
        const w = wordOf(ctx, e);
        if (!w || !canShowPicture(w)) continue;
        const found = soundalikePartner(ctx, e, w);
        if (!found) continue;
        // Four baskets, not two. With two, half the shells go in the right one by coin toss and
        // a child who never hears the contrast still finishes the beach; the sound the format
        // exists to teach can be ignored entirely. The taught pair leads, and the lesson's other
        // pictures fill the beach out — sound-alikes of either of the pair first, because a near
        // miss is a better wrong basket than an unrelated one.
        const baskets: Word[] = [w, found.partner];
        const spare = shuffle(
          lessonPictureWords(ctx).filter((x) => !baskets.some((y) => y.id === x.id)),
          rng,
        );
        const take = (x: Word) => {
          if (baskets.length < 4 && !baskets.some((y) => y.id === x.id)) baskets.push(x);
        };
        const sy = taughtSyllable(ctx, e, w);
        for (const x of spare) if (soundsAlike(w, x, sy) || soundsAlike(found.partner, x)) take(x);
        for (const x of spare) take(x);
        if (baskets.length < 4) continue;
        // Shells come from the taught pair, which is what the round is about; the other baskets
        // are there to be ruled out. Never the same shell twice running.
        const shells: string[] = [];
        let last = '';
        while (shells.length < 8) {
          const pick = rng() < 0.5 ? w.id : found.partner.id;
          if (pick === last && rng() < 0.7) continue;
          shells.push(pick);
          last = pick;
        }
        for (let step = 0; step < shells.length; step++) {
          const { entry, word } = targetOf(ctx, entries, shells[step]!, e, w);
          out.push({
            id: `${qid('listen_sort', e)}:${step}`,
            entry,
            word,
            skill: 'pinyin',
            type: 'listen_sort',
            baskets,
            shells,
            contrast: found.contrast,
            step,
          });
        }
        // One beach (eight shells) is a stop; a second was sixteen screens of the same game.
        if (out.length >= 8) break;
      }
      return out;
    }
    case 'tone_slide':
      return compact(entries.map((e) => withFrame(q('tone', e), { frame: 'tone_slide' })));
    case 'tone_paint': {
      // The ramp by lesson: arrows only, then draw over the shown contour, then draw blind.
      const order = effectiveLessonOrder(ctx.bundle.lesson.levelId, ctx.bundle.lesson.order);
      const toneMode = order < 2 ? 'arrows' : order === 2 ? 'draw_shown' : 'draw';
      return compact(entries.map((e) => q('tone_draw', e, { toneMode })));
    }
    case 'builder': {
      const out: Question[] = [];
      const lessonChars = lessonCharSet(ctx);
      for (const e of entries) {
        const comp = ctx.bundle.characters[e.char]?.components;
        if (!comp) continue;
        // One distractor part from another compound of the lesson, else any other part.
        const pool = Object.values(ctx.bundle.characters)
          .filter((c) => c.char !== e.char && c.components && lessonChars.has(c.char))
          .flatMap((c) => c.components!.parts)
          .filter((p) => !comp.parts.includes(p));
        // The ramp: the first character has only its own parts, later ones add a decoy.
        const distractor =
          out.length > 0 && pool.length ? shuffle([...new Set(pool)], rng)[0]! : null;
        const base = qf('picture_to_hanzi', e, { choices: 3 });
        if (!base) continue;
        out.push({
          ...base,
          skill: 'meaning',
          frame: { frame: 'builder', ids: comp.ids, parts: comp.parts, distractor },
        });
      }
      return out;
    }
    case 'bridge': {
      const out: Question[] = [];
      for (const e of entries) {
        const w = wordOf(ctx, e);
        if (!w) continue;
        const found = readSentenceFor(ctx, w);
        if (!found) continue;
        const idx = contentTokenIdx(found.sentence.tokens);
        if (idx.length < 2 || idx.length > 5) continue;
        const base = q('sentence_fill', e, { choices: 3 });
        if (!base) continue;
        // Two planks until lesson 4 (the sentence in two chunks), three from lesson 4, and never
        // more planks than tokens.
        const chunks = chunkTokens(
          idx,
          effectiveLessonOrder(ctx.bundle.lesson.levelId, ctx.bundle.lesson.order) < 4 ? 2 : 3,
          found.sentence.tokens.map((t) => t.text),
        );
        const order = scrambled(
          chunks.map((_, i) => i),
          rng,
        );
        out.push({ ...base, frame: { frame: 'bridge', chunks, order } });
      }
      return out;
    }
    case 'reveal':
      return compact(
        writers.map((e) => withFrame(q('trace', e, { trace: 'memory' }), { frame: 'reveal' })),
      );
    case 'stroke_missing':
      return compact(writers.map((e) => q('stroke_missing', e)));
    case 'flash':
      return compact(writers.map((e) => q('trace', e, { trace: 'flash' })));
    case 'stroke_order':
      // The ramp: fewest strokes first.
      return compact(
        [...writers]
          .sort(
            (a, b) =>
              (ctx.bundle.characters[a.char]?.strokeCount ?? 0) -
              (ctx.bundle.characters[b.char]?.strokeCount ?? 0),
          )
          .map((e) => q('stroke_order', e)),
      );
  }
}

/** The syllable a word's taught character carries, for labels in the lab. */
export const taughtPinyin = (ctx: BuildContext, e: CharacterEntry): string | null => {
  const w = wordOf(ctx, e);
  return syllableInWord(e.char, w, ctx.bundle.characters[e.char]);
};
